import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../axios/api.js';

// Thunks backing the `vendor/:id` route (VendorDetails) and its child dialogs.

const asMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

// ---------------------------------------------------------------- vendor

export const fetchVendorDetails = createAsyncThunk(
  'vendorDetails/fetchVendorDetails',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/vendordata/${id}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(
        asMessage(error, 'Error fetching company details. Please try again later.')
      );
    }
  }
);

export const updateVendorGst = createAsyncThunk(
  'vendorDetails/updateVendorGst',
  async ({ gst_no, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/companygst/', { gst_no, id });
      dispatch(fetchVendorDetails(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Vendor GST . Please try again.'));
    }
  }
);

export const updateVendorAadhar = createAsyncThunk(
  'vendorDetails/updateVendorAadhar',
  async ({ aadhar_no, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/companyaadhar/', { aadhar_no, id });
      dispatch(fetchVendorDetails(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        asMessage(error, 'Unable to edit Vendor Aadhar Card No. Please try again.')
      );
    }
  }
);

export const updateVendorEmail = createAsyncThunk(
  'vendorDetails/updateVendorEmail',
  async ({ vendor_email, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/companyemail/', { vendor_email, id });
      dispatch(fetchVendorDetails(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Vendor Email. Please try again.'));
    }
  }
);

// ------------------------------------------------------- type / country

export const fetchVendorCompanyType = createAsyncThunk(
  'vendorDetails/fetchVendorCompanyType',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/vendorcompanytype', { params: { id } });
      if (response.status === 200 && response.data.length > 0 && response.data[0].company_type) {
        return response.data[0].company_type;
      }
      return [];
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching cards'));
    }
  }
);

export const addVendorCompanyType = createAsyncThunk(
  'vendorDetails/addVendorCompanyType',
  async ({ company_type, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/vendorcompanytypeadd/', { company_type, id });
      dispatch(fetchVendorCompanyType(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Overview. Please try again.'));
    }
  }
);

export const editVendorCompanyType = createAsyncThunk(
  'vendorDetails/editVendorCompanyType',
  async ({ company_type, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/vendorcompanytypeedit/', { company_type, id });
      dispatch(fetchVendorCompanyType(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Overview. Please try again.'));
    }
  }
);

export const fetchVendorCountry = createAsyncThunk(
  'vendorDetails/fetchVendorCountry',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/vendorcountry', { params: { id } });
      if (response.status === 200 && response.data.length > 0 && response.data[0].vendor_country) {
        return response.data[0].vendor_country;
      }
      return [];
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching country'));
    }
  }
);

export const addVendorCountry = createAsyncThunk(
  'vendorDetails/addVendorCountry',
  async ({ vendor_country, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/vendorcountryadd/', { vendor_country, id });
      dispatch(fetchVendorCountry(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Overview. Please try again.'));
    }
  }
);

export const editVendorCountry = createAsyncThunk(
  'vendorDetails/editVendorCountry',
  async ({ vendor_country, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/vendorcountryedit/', { vendor_country, id });
      dispatch(fetchVendorCountry(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Overview. Please try again.'));
    }
  }
);

export const addVendorDesignation = createAsyncThunk(
  'vendorDetails/addVendorDesignation',
  async ({ designation, id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/vendordesignationadd/', { designation, id });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to add designation. Please try again.'));
    }
  }
);

// Shared by VendorDescriptionCard and AddDescriptionDialog.
export const saveVendorDescription = createAsyncThunk(
  'vendorDetails/saveVendorDescription',
  async ({ vendorId, description }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/vendor-trial/description', {
        vendor_id: vendorId,
        description,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        asMessage(error, 'Unable to update Vendor Description. Please try again.')
      );
    }
  }
);

export const fetchTuneInData = createAsyncThunk(
  'vendorDetails/fetchTuneInData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/tuneindata/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch tune-in data'));
    }
  }
);

// -------------------------------------------------------------- contacts

// The list endpoint returns product/category/material as either a JSON string,
// a comma-joined string, or an array — normalise once here.
const parseList = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        return JSON.parse(val);
      } catch (e) {
        console.warn('Failed to parse JSON for contact field:', val);
      }
    }
    return val.split(', ').filter(Boolean);
  }
  return [];
};

const transformContacts = (rows) =>
  rows.map((contact) => ({
    ...contact,
    product: parseList(contact.product),
    category: parseList(contact.category),
    material: parseList(contact.material),
  }));

export const fetchVendorContacts = createAsyncThunk(
  'vendorDetails/fetchVendorContacts',
  async ({ id, limit, offset, search }, { rejectWithValue }) => {
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await axiosClient.get(
        `/api/allvendordata/${id}?limit=${limit}&offset=${offset}${searchParam}`
      );
      if (response.data.data) {
        return { data: transformContacts(response.data.data), total: response.data.total };
      }
      return {
        data: transformContacts(response.data),
        total: response.data.length,
      };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch contacts'));
    }
  }
);

export const addVendorContact = createAsyncThunk(
  'vendorDetails/addVendorContact',
  async ({ companyallcontacts, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/addvendorallcontacts/${companyallcontacts}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Adding Contact error'));
    }
  }
);

export const fetchVendorContactToEdit = createAsyncThunk(
  'vendorDetails/fetchVendorContactToEdit',
  async (currentallcontacts_id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/toeditallvendordata/${currentallcontacts_id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch contact'));
    }
  }
);

export const updateVendorContact = createAsyncThunk(
  'vendorDetails/updateVendorContact',
  async ({ currentallcontactsid, editedContacts }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/vendorallcontacts/${currentallcontactsid}`, {
        editedContacts,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Edit contact error'));
    }
  }
);

// Rejects with `{ conflict: true, ...usage }` on 409 so the caller can open the
// reassign dialog instead of showing a generic failure.
export const deleteVendorContact = createAsyncThunk(
  'vendorDetails/deleteVendorContact',
  async (contactId, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendorallcontacts/${contactId}`);
      return contactId;
    } catch (error) {
      if (error.response?.status === 409) {
        return rejectWithValue({ conflict: true, ...error.response.data, contactId });
      }
      return rejectWithValue({ message: asMessage(error, 'Delete Contact error') });
    }
  }
);

export const uploadVendorContacts = createAsyncThunk(
  'vendorDetails/uploadVendorContacts',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/allcontactsvendorupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

// ------------------------------------------------------ shipping/billing

export const fetchVendorShippingData = createAsyncThunk(
  'vendorDetails/fetchVendorShippingData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/vendorshippingdata/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch shipping addresses'));
    }
  }
);

export const fetchVendorBillingData = createAsyncThunk(
  'vendorDetails/fetchVendorBillingData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/vendorbillingdata/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch billing addresses'));
    }
  }
);

export const addVendorShippingAddress = createAsyncThunk(
  'vendorDetails/addVendorShippingAddress',
  async ({ companynameshippingaddress, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/vendorshippingaddress/${companynameshippingaddress}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Shipping address add error'));
    }
  }
);

export const addVendorBillingAddress = createAsyncThunk(
  'vendorDetails/addVendorBillingAddress',
  async ({ companynamesbillingaddress, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/vendorbillingaddress/${companynamesbillingaddress}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Billing address add error'));
    }
  }
);

export const fetchVendorShippingToEdit = createAsyncThunk(
  'vendorDetails/fetchVendorShippingToEdit',
  async (shippingid, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/editvendorshippingdata/${shippingid}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch shipping address'));
    }
  }
);

export const fetchVendorBillingToEdit = createAsyncThunk(
  'vendorDetails/fetchVendorBillingToEdit',
  async (billingid, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/editvendorbillingdata/${billingid}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch billing address'));
    }
  }
);

export const editVendorShippingAddress = createAsyncThunk(
  'vendorDetails/editVendorShippingAddress',
  async ({ editshippingaddressdata, shippingid }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/vendorshippingedit', {
        editshippingaddressdata,
        shippingid,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Shipping address edit error'));
    }
  }
);

export const editVendorBillingAddress = createAsyncThunk(
  'vendorDetails/editVendorBillingAddress',
  async ({ editbillingaddressdata, billingid }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/vendorbillingaddressedit', {
        editbillingaddressdata,
        billingid,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Billing address edit error'));
    }
  }
);

export const deleteVendorShippingAddress = createAsyncThunk(
  'vendorDetails/deleteVendorShippingAddress',
  async (shippingid, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendorshippingaddress/${shippingid}`);
      return shippingid;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete shipping address error'));
    }
  }
);

export const deleteVendorBillingAddress = createAsyncThunk(
  'vendorDetails/deleteVendorBillingAddress',
  async (billingid, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendorbillingaddress/${billingid}`);
      return billingid;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete billing address error'));
    }
  }
);

export const uploadVendorShippingAddresses = createAsyncThunk(
  'vendorDetails/uploadVendorShippingAddresses',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/vendorshippingupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

export const uploadVendorBillingAddresses = createAsyncThunk(
  'vendorDetails/uploadVendorBillingAddresses',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/vendorbillingupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

// ---------------------------------------------------------- certificates

export const fetchVendorCertificates = createAsyncThunk(
  'vendorDetails/fetchVendorCertificates',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/certificatesdata/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch certificates'));
    }
  }
);

export const fetchVendorArchivedCertificates = createAsyncThunk(
  'vendorDetails/fetchVendorArchivedCertificates',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/certificatesarchivedata/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch archived certificates'));
    }
  }
);

export const fetchVendorFilteredCertificates = createAsyncThunk(
  'vendorDetails/fetchVendorFilteredCertificates',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/certificatesfilterdata/', { id, status });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch certificates'));
    }
  }
);

export const uploadVendorCertificate = createAsyncThunk(
  'vendorDetails/uploadVendorCertificate',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/certificate-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

export const fetchVendorCertificateToEdit = createAsyncThunk(
  'vendorDetails/fetchVendorCertificateToEdit',
  async (certificateId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/toeditcertificatesdata/${certificateId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch certificate'));
    }
  }
);

export const editVendorCertificate = createAsyncThunk(
  'vendorDetails/editVendorCertificate',
  async ({ certificateId, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/vendoreditcertificates/${certificateId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Edit certificate error'));
    }
  }
);

export const deleteVendorCertificate = createAsyncThunk(
  'vendorDetails/deleteVendorCertificate',
  async (certificateId, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendorcertificates/${certificateId}`);
      return certificateId;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete certificate error'));
    }
  }
);

// ------------------------------------------------------------------ RFQ

export const fetchVendorLeads = createAsyncThunk(
  'vendorDetails/fetchVendorLeads',
  async ({ id, limit, offset, search }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/vendordetailsrfq', {
        id,
        limit,
        offset,
        search,
      });
      return { ...response.data, offset };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching leads'));
    }
  }
);

// ------------------------------------------------------------ reference

export const fetchVendorCountryCodeOptions = createAsyncThunk(
  'vendorDetails/fetchVendorCountryCodeOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/allcompanycountrydata/');
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch country codes'));
    }
  }
);

export const fetchVendorCountryDialCode = createAsyncThunk(
  'vendorDetails/fetchVendorCountryDialCode',
  async (country, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/api/get-country-code?country=${encodeURIComponent(country)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch country dial code'));
    }
  }
);

export const fetchVendorLocationData = createAsyncThunk(
  'vendorDetails/fetchVendorLocationData',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/vendorlocationdata/${companyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching location data'));
    }
  }
);

export const fetchVendorMaterialCodeFilters = createAsyncThunk(
  'vendorDetails/fetchVendorMaterialCodeFilters',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/vendormaterialcodedata-filters/${id}`);
      return response.data?.options ?? null;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch product filter options'));
    }
  }
);

export const fetchVendorMaterialCodeData = createAsyncThunk(
  'vendorDetails/fetchVendorMaterialCodeData',
  async ({ id, page, limit, filters }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/vendormaterialcodedata-paged/${id}`, {
        params: {
          page,
          limit,
          filters: filters && Object.keys(filters).length ? JSON.stringify(filters) : undefined,
        },
      });
      const materialCode = response.data?.material_code || [];
      const safeMaterialCode = Array.isArray(materialCode) ? materialCode : [];
      const totalValue = Number(response.data?.total);
      return {
        materialCode: safeMaterialCode,
        total: Number.isFinite(totalValue) ? totalValue : safeMaterialCode.length,
      };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch material code data'));
    }
  }
);

const TEMPLATE_ENDPOINTS = {
  allContacts: '/fetch-allcontacts-documentformat',
  address: '/fetch-address-document-format',
  clients: '/fetch-clients-documentformat',
};

export const fetchVendorDetailsTemplateUrl = createAsyncThunk(
  'vendorDetails/fetchVendorDetailsTemplateUrl',
  async (kind, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(TEMPLATE_ENDPOINTS[kind]);
      return { kind, url: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch template URL'));
    }
  }
);

const initialState = {
  // The page keeps the vendor as a one-element array, matching the endpoint.
  company: [],
  companyLoading: false,
  companyError: null,

  cards: [],
  vendorCountry: [],
  tuneInData: [],

  // Contacts
  allContacts: [],
  totalContacts: 0,
  contactsLoading: false,
  contactToEdit: [],
  reassignUsageData: null,

  // Addresses
  shippingAddressData: [],
  billingAddressData: [],
  shippingToEdit: [],
  billingToEdit: [],

  // Certificates
  certificates: [],
  certificateToEdit: [],

  // RFQ
  leads: [],
  rfqTotal: 0,
  rfqLoading: false,

  // Material-code table
  materialData: [],
  productTotal: 0,
  productLoading: false,
  productFilterOptions: null,

  // Reference data
  countryCodeOptions: [],
  locationOptions: [],
  templateUrls: { allContacts: '', address: '', clients: '' },

  error: null,
};

const vendorDetailsSlice = createSlice({
  name: 'vendorDetails',
  initialState,
  reducers: {
    setVendorCompany: (state, action) => {
      state.company = action.payload;
    },
    setVendorCards: (state, action) => {
      state.cards = action.payload;
    },
    setVendorCountryLocal: (state, action) => {
      state.vendorCountry = action.payload;
    },
    setVendorContacts: (state, action) => {
      state.allContacts = action.payload;
    },
    setVendorContactToEdit: (state, action) => {
      state.contactToEdit = action.payload;
    },
    setVendorCertificates: (state, action) => {
      state.certificates = action.payload;
    },
    setVendorCertificateToEdit: (state, action) => {
      state.certificateToEdit = action.payload;
    },
    setVendorReassignUsageData: (state, action) => {
      state.reassignUsageData = action.payload;
    },
    setVendorShippingToEdit: (state, action) => {
      state.shippingToEdit = action.payload;
    },
    setVendorBillingToEdit: (state, action) => {
      state.billingToEdit = action.payload;
    },
    setVendorLocationOptions: (state, action) => {
      state.locationOptions = action.payload;
    },
    setVendorLeads: (state, action) => {
      state.leads = action.payload;
    },
    setVendorMaterialData: (state, action) => {
      state.materialData = action.payload;
    },
    setVendorTuneInData: (state, action) => {
      state.tuneInData = action.payload;
    },
    // Clears the per-vendor material-code table when the vendor changes.
    resetVendorProductTable: (state) => {
      state.materialData = [];
      state.productTotal = 0;
      state.productFilterOptions = null;
    },
    clearVendorDetailsError: (state) => {
      state.error = null;
      state.companyError = null;
    },
    resetVendorDetailsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendorDetails.pending, (state) => {
        state.companyLoading = true;
        state.companyError = null;
      })
      .addCase(fetchVendorDetails.fulfilled, (state, action) => {
        state.companyLoading = false;
        state.company = action.payload;
      })
      .addCase(fetchVendorDetails.rejected, (state, action) => {
        state.companyLoading = false;
        state.companyError = action.payload;
        state.company = [];
      })

      .addCase(fetchVendorCompanyType.fulfilled, (state, action) => {
        state.cards = action.payload;
      })
      .addCase(fetchVendorCompanyType.rejected, (state) => {
        state.cards = [];
      })
      .addCase(fetchVendorCountry.fulfilled, (state, action) => {
        state.vendorCountry = action.payload;
      })
      .addCase(fetchVendorCountry.rejected, (state) => {
        state.vendorCountry = [];
      })
      .addCase(fetchTuneInData.fulfilled, (state, action) => {
        state.tuneInData = action.payload;
      })
      .addCase(fetchTuneInData.rejected, (state) => {
        state.tuneInData = [];
      })

      // Contacts
      .addCase(fetchVendorContacts.pending, (state) => {
        state.contactsLoading = true;
      })
      .addCase(fetchVendorContacts.fulfilled, (state, action) => {
        state.contactsLoading = false;
        state.allContacts = action.payload.data;
        state.totalContacts = action.payload.total;
      })
      .addCase(fetchVendorContacts.rejected, (state) => {
        state.contactsLoading = false;
        state.allContacts = [];
      })
      .addCase(fetchVendorContactToEdit.fulfilled, (state, action) => {
        state.contactToEdit = action.payload;
      })
      .addCase(deleteVendorContact.rejected, (state, action) => {
        if (action.payload?.conflict) {
          state.reassignUsageData = action.payload;
        } else {
          state.error = action.payload?.message;
        }
      })

      // Addresses
      .addCase(fetchVendorShippingData.fulfilled, (state, action) => {
        state.shippingAddressData = action.payload;
      })
      .addCase(fetchVendorBillingData.fulfilled, (state, action) => {
        state.billingAddressData = action.payload;
      })
      .addCase(fetchVendorShippingToEdit.fulfilled, (state, action) => {
        state.shippingToEdit = action.payload;
      })
      .addCase(fetchVendorBillingToEdit.fulfilled, (state, action) => {
        state.billingToEdit = action.payload;
      })

      // Certificates
      .addCase(fetchVendorCertificates.fulfilled, (state, action) => {
        state.certificates = action.payload;
      })
      .addCase(fetchVendorCertificates.rejected, (state) => {
        state.certificates = [];
      })
      .addCase(fetchVendorArchivedCertificates.fulfilled, (state, action) => {
        state.certificates = action.payload;
      })
      .addCase(fetchVendorArchivedCertificates.rejected, (state) => {
        state.certificates = [];
      })
      .addCase(fetchVendorFilteredCertificates.fulfilled, (state, action) => {
        state.certificates = action.payload;
      })
      .addCase(fetchVendorCertificateToEdit.fulfilled, (state, action) => {
        state.certificateToEdit = action.payload;
      })

      // RFQ — offset 0 replaces the page, later offsets append (infinite scroll)
      .addCase(fetchVendorLeads.pending, (state) => {
        state.rfqLoading = true;
      })
      .addCase(fetchVendorLeads.fulfilled, (state, action) => {
        state.rfqLoading = false;
        const { enrichedRows = [], total = 0, offset } = action.payload;
        if (offset === 0) {
          state.leads = enrichedRows;
        } else {
          // Batches can overlap, so drop rows already held.
          const seen = new Set(state.leads.map((l) => l.rfq_no));
          state.leads = [...state.leads, ...enrichedRows.filter((l) => !seen.has(l.rfq_no))];
        }
        state.rfqTotal = total;
      })
      .addCase(fetchVendorLeads.rejected, (state, action) => {
        state.rfqLoading = false;
        state.error = action.payload;
      })

      // Material-code table
      .addCase(fetchVendorMaterialCodeFilters.fulfilled, (state, action) => {
        state.productFilterOptions = action.payload;
      })
      .addCase(fetchVendorMaterialCodeData.pending, (state) => {
        state.productLoading = true;
      })
      .addCase(fetchVendorMaterialCodeData.fulfilled, (state, action) => {
        state.productLoading = false;
        state.materialData = action.payload.materialCode;
        state.productTotal = action.payload.total;
      })
      .addCase(fetchVendorMaterialCodeData.rejected, (state) => {
        state.productLoading = false;
      })

      // Reference data
      .addCase(fetchVendorCountryCodeOptions.fulfilled, (state, action) => {
        state.countryCodeOptions = action.payload;
      })
      .addCase(fetchVendorDetailsTemplateUrl.fulfilled, (state, action) => {
        state.templateUrls[action.payload.kind] = action.payload.url;
      });
  },
});

export const {
  setVendorCompany,
  setVendorCards,
  setVendorCountryLocal,
  setVendorContacts,
  setVendorContactToEdit,
  setVendorCertificates,
  setVendorCertificateToEdit,
  setVendorReassignUsageData,
  setVendorShippingToEdit,
  setVendorBillingToEdit,
  setVendorLocationOptions,
  setVendorLeads,
  setVendorMaterialData,
  setVendorTuneInData,
  resetVendorProductTable,
  clearVendorDetailsError,
  resetVendorDetailsState,
} = vendorDetailsSlice.actions;

export default vendorDetailsSlice.reducer;
